import React, { useMemo } from "react";
import styled from "styled-components";
import { SafetyCertificateOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { getMarketRegionFromZip } from "../lib/regionFromZip";

/**
 * Regional market pulse: benchmarks and freshness for the homeowner's ZIP / metro.
 */
function RegionalMarketPulse({
  regionLabel: regionLabelProp,
  zipCode = null,
  laborLine = null,
  savingsLine = null,
  freshness = "High",
  className = "",
  size = "default",
}) {
  const regionLabel = useMemo(() => {
    if (regionLabelProp && String(regionLabelProp).trim()) {
      return String(regionLabelProp).trim();
    }
    return getMarketRegionFromZip(zipCode);
  }, [regionLabelProp, zipCode]);

  const title = `${String(regionLabel).toUpperCase()} MARKET PULSE`;
  const zipBadge = zipCode
    ? `Benchmarked to ZIP ${String(zipCode).replace(/\D/g, "").slice(0, 5)}`
    : "Benchmarked to your local market";

  const currentDate = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <PulseContainer className={className} $size={size}>
      <PulseContent $size={size}>
        <PulseTitleRow $size={size}>{title}</PulseTitleRow>
        <ZipBadge $size={size}>📍 {zipBadge}</ZipBadge>
        <MetaRow>
          <span>Index as of {currentDate}</span>
          <FreshnessIndicator $freshness={freshness} $size={size}>
            <FreshnessIcon $freshness={freshness}>
              <SafetyCertificateOutlined />
            </FreshnessIcon>
            <FreshnessText $size={size}>Freshness: {freshness}</FreshnessText>
          </FreshnessIndicator>
        </MetaRow>
        {(laborLine || savingsLine) && (
          <IntelBlock>
            {laborLine ? <IntelLine>{laborLine}</IntelLine> : null}
            {savingsLine ? <IntelLine>{savingsLine}</IntelLine> : null}
          </IntelBlock>
        )}
      </PulseContent>
      <TooltipTrigger $size={size}>
        <InfoCircleOutlined />
      </TooltipTrigger>
    </PulseContainer>
  );
}

export default RegionalMarketPulse;

const PulseContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size",
})`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${(props) => (props.$size === "compact" ? "8px" : "12px")};
  padding: ${(props) => (props.$size === "compact" ? "6px 8px" : "8px 12px")};
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${(props) => (props.$size === "compact" ? "6px" : "8px")};
  backdrop-filter: blur(8px);
  font-family: var(--font-mono, "IBM Plex Mono", "SF Mono", "Monaco", monospace);
  font-weight: 500;
`;

const PulseContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size",
})`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${(props) => (props.$size === "compact" ? "4px" : "6px")};
  flex: 1;
  min-width: 0;
`;

const PulseTitleRow = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size",
})`
  font-size: ${(props) => (props.$size === "compact" ? "0.62rem" : "0.7rem")};
  font-weight: 800;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.96);
  line-height: 1.25;
  white-space: normal;
`;

const ZipBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size",
})`
  font-size: ${(props) => (props.$size === "compact" ? "0.58rem" : "0.65rem")};
  font-weight: 600;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.3;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 0.65rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.72);
`;

const IntelBlock = styled.div`
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 100%;
`;

const IntelLine = styled.div`
  font-size: 0.62rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.35;
`;

const FreshnessIndicator = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size" && prop !== "$freshness",
})`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const FreshnessIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size" && prop !== "$freshness",
})`
  display: flex;
  align-items: center;
  justify-content: center;

  .anticon {
    font-size: ${(props) =>
      props.$size === "compact" ? "0.75rem" : "0.8125rem"};
    color: ${(props) => {
      switch (props.$freshness) {
        case "High":
          return "#10b981";
        case "Medium":
          return "#f59e0b";
        case "Low":
          return "#ef4444";
        default:
          return "#10b981";
      }
    }};
  }
`;

const FreshnessText = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "$size",
})`
  font-size: ${(props) => (props.$size === "compact" ? "0.625rem" : "0.6875rem")};
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

const TooltipTrigger = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$size",
})`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }

  .anticon {
    font-size: ${(props) =>
      props.$size === "compact" ? "0.75rem" : "0.8125rem"};
    color: rgba(255, 255, 255, 0.8);
  }
`;
